import { api } from './client'
import type { FuelTransaction, FuelTransactionCreate } from '@/types/fuel'

export const fuelApi = {
  async getAll(): Promise<FuelTransaction[]> {
    const resp = await api.get('/equipment/fuel-transactions/')
    const data: unknown = resp.data

    // Normalize common shapes: array, paginated { results: [...] }, or { data: [...] }
    if (Array.isArray(data)) return data as FuelTransaction[]
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>
      if (Array.isArray(obj.results)) return obj.results as FuelTransaction[]
      if (Array.isArray(obj.data)) return obj.data as FuelTransaction[]
      if (Array.isArray(obj.items)) return obj.items as FuelTransaction[]

      // fallback: first array-valued property
      for (const k of Object.keys(obj)) {
        const v = obj[k]
        if (Array.isArray(v)) return v as FuelTransaction[]
      }
    }

    // Unexpected shape -> return empty array to keep UI stable
    console.warn('[fuelApi] getAll: unexpected response shape', data)
    return []
  },

  async getById(id: number): Promise<FuelTransaction> {
    const response = await api.get(`/equipment/fuel-transactions/${id}/`)
    return response.data
  },

  async create(data: FuelTransactionCreate): Promise<FuelTransaction> {
    // Basic client-side sanity validation to catch obvious mistakes early
    try {
      if (!data || !data.equipment_id || Number(data.equipment_id) <= 0) {
        throw new Error('Invalid `equipment_id` (must be a positive integer)')
      }
      if (data.liters !== undefined && Number(data.liters) <= 0) {
        throw new Error('`liters` must be greater than zero')
      }
      if (data.cost_per_liter !== undefined && Number(data.cost_per_liter) <= 0) {
        throw new Error('`cost_per_liter` must be greater than zero')
      }
    } catch (validationErr) {
      // Throw early so caller can handle this as a regular error
      return Promise.reject(validationErr)
    }

    try {
      const response = await api.post('/equipment/fuel-transactions/', data)
      return response.data
    } catch (err: unknown) {
      // Log server response body for easier debugging in the browser console
      try {
        const maybeResp = err as { response?: { data?: unknown } } | undefined
        const serverData = maybeResp?.response?.data
        console.error('[fuelApi.create] server response', serverData)
      } catch {}
      return Promise.reject(err)
    }
  },

  async update(id: number, data: Partial<FuelTransactionCreate>): Promise<FuelTransaction> {
    const response = await api.patch(`/equipment/fuel-transactions/${id}/`, data)
    return response.data
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/equipment/fuel-transactions/${id}/`)
  },
}
